import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import * as api from "../../../../backend/request";
import { toast } from 'react-toastify';
import PageLoader from "../../../shared/PageLoader";
import { Link, usePath, useHistory, navigate } from "raviger";
import Table, { CellTmpl, DEFAULT_PAYLOAD, TitleTmpl, reactFormatter } from "../../../common/Table";
import { TOOLTIP_DELAY } from "../../../common/Constants";
import Location from "../../../common/Location";
import { useGetAllLocationsAudits } from "../../../../backend/query";
import Icon from "../../../common/Icon";
import { downloadFileContent, preventDefault } from "../../../../utils/common";
import OverlayTrigger from "react-bootstrap/OverlayTrigger"
import Tooltip from 'react-bootstrap/Tooltip';
import { ERROR_MESSAGES } from "../../../../utils/constants";
import { getAuditReportFileName, useAuditReport } from "../../../../backend/exports";
import { AUDIT_STATUS_COLOR_CODES, AUDIT_STATUS_LABELS, AUDIT_STATUS as LOCATION_AUDIT_STATUS } from "../../../../utils/auditStateMachine";

const SortFields: any = {
    'act.name': 'act_name',
    'rule.name': 'rule_name',
    'activity.name': 'activity_name',
    'activity.activity_type': 'activity_activity_type',
    'associate_company.name': 'associate_company_name',
    'location.name': 'location_name'
};

// const AuditTypeFilter = [{ columnName: 'auditted', value: ACTIVITY_TYPE.AUDIT }];
const AuditTypeFilter: any[] = [];

function getAdvanceSearch(state: any) {
    const filters: any = {};
    if ((state || {}).fromDate) {
        filters['from_date_gteq'] = dayjs(state.fromDate).startOf('D').toISOString()
        filters['to_date'] = dayjs(state.toDate).endOf('D').toISOString()
    }

    if ((state || {}).audit_type) {
        filters['audit_type'] = state.auditType
    }
    return filters || undefined;
}

function AuditList() {
    const { state }: any = useHistory();
    const [submitting, setSubmitting] = useState(false);
    const path: any = usePath();
    const [fromDashboard] = useState(path.includes('/dashboard/activities'));
    const [data, setData] = useState<any>();
    const [params, setParams] = useState<any>();
    const [locationFilters, setLocationFilter] = useState<any>();
    const lfRef = useRef();
    lfRef.current = locationFilters;
    const [advaceSearchFilters, setAdvanceSearchFilters] = useState(getAdvanceSearch(state));
    const afRef: any = useRef();
    afRef.current = advaceSearchFilters;
    const [statusFilters, setStatusFilters] = useState<any>();
    const sfRef = useRef();
    sfRef.current = statusFilters;
    const [payload, setPayload] = useState<any>();
    const payloadRef = useRef();
    payloadRef.current = payload;
    const [previousFilters, setPreviousFilters] = useState<any>(state || {});
    const previousFiltersRef = useRef();
    previousFiltersRef.current = previousFilters;
    const [selectedReport, setSelectedReport] = useState<any>(null);
    const { activities, total, isFetching, refetch } = useGetAllLocationsAudits(payload, Boolean(hasFilters(null, 'company_id_eq')));
    const { auditReport, exporting } = useAuditReport((response: any) => {
        if (response) {
            const filename = selectedReport && selectedReport.name ? selectedReport.name.charAt(0).toUpperCase() + selectedReport.name.slice(1) : 'audit_report';
            downloadFileContent({
                name: filename,
                type: response.headers['content-type'],
                content: response.data
            });
        }
    }, () => {
        toast.error(ERROR_MESSAGES.DEFAULT)
    });

    function onLocationChange(event: any, filter: any = {}) {
        const { company, associate_company, location } = event;
        setLocationFilter({
            company_id_eq: company,
            associate_company_id_eq: associate_company,
            location_id_eq: location
        });
        if(Object.keys(filter).length > 0){
            const _filter = {
                ...previousFilters,
                ...filter
            }
            setPreviousFilters(_filter);
        }
    }

    async function downloadReport(audit: any) {
        if (audit && audit.id) {
          setSubmitting(true);
          try {
            let reportData: any;
            let auditData: any;
            if (audit && (audit.preliminary_report || audit.final_report)) {
              auditData = audit;
            } else {
              const response = await api.get(`/api/v1/location_audits/${audit.id}`, {
                include: ['preliminary_report', 'final_report'],
                includes: ['preliminary_report', 'final_report']
              });
              auditData = response.data;
            }
            if([LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_FINALIZED, LOCATION_AUDIT_STATUS.FINAL_AUDIT_PENDING, LOCATION_AUDIT_STATUS.FINAL_AUDIT_SUBMITTED, LOCATION_AUDIT_STATUS.FINAL_AUDIT_EDITING].includes(audit.status)){
              reportData = auditData.preliminary_report;
            } else if([LOCATION_AUDIT_STATUS.FINAL_AUDIT_PUBLISHED, LOCATION_AUDIT_STATUS.FINAL_AUDIT_REPORT_UPLOADED].includes(audit.status)){
              reportData = auditData.final_report;
            }
            setSelectedReport(reportData);
    
            if (!reportData) {
              toast.warn(`No report available`);
              setSubmitting(false);
              return;
            }
    
            const { s3_region, s3_key, s3_bucket } = reportData;
            if (s3_region && s3_key && s3_bucket) {
              auditReport({
                s3_region,
                s3_key,
                s3_bucket
              });
            } else {
              toast.warn(`No report available`);
            }
          } catch (error) {
            console.error('Error fetching audit:', error);
          }
          setSubmitting(false);
        }
    }

    function MonthTmpl({ cell }: any) {
        const row = cell.getData();
        return (
            <div className="d-flex align-items-center position-relative h-100">{row.month} ({row.year})</div>
        )
    }

    function AuditStatusTmpl({ cell }: any) {
    const status: any = cell.getValue();
    return (
      <div className="d-flex align-items-center position-relative h-100">
        {status && (
          <OverlayTrigger overlay={<Tooltip>{AUDIT_STATUS_LABELS[status as keyof typeof AUDIT_STATUS_LABELS] || status}</Tooltip>} rootClose={true}
              placement="bottom" delay={{ show: TOOLTIP_DELAY } as any}>
              <div className={`ellipse two-lines ${AUDIT_STATUS_COLOR_CODES[status as keyof typeof AUDIT_STATUS_COLOR_CODES] || 'text-dark-gray'}`}>{AUDIT_STATUS_LABELS[status as keyof typeof AUDIT_STATUS_LABELS] || status}</div>
          </OverlayTrigger>
        )}
      </div>
    );
  }

    function ActionColumnElements({ cell }: any) {
        const row = cell.getData();

        return (
            <div className="d-flex flex-row align-items-center position-relative h-100">
                {/* <Icon className="mx-2" type="button" name={hasUserAccess(USER_PRIVILEGES.SUBMITTER_ACTIVITIES_UPLOAD) ? 'pencil' : 'eye'}
                    text={hasUserAccess(USER_PRIVILEGES.SUBMITTER_ACTIVITIES_UPLOAD) ? 'Edit' : 'View'} data={row} action={editActivity} /> */}
                <Icon className="ms-1" type="button" name="download" text="Download" data={row} action={downloadReport} />
            </div>
        )
    }

    const columns = [
        {
            title: "Month & Year", field: "month", width: 160, headerSort: false,
            formatter: reactFormatter(<MonthTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            headerClick: (e: any, col: any) => {
                preventDefault(e);
            }
        },
        {
            title: "Company", field: "company.name", widthGrow: 2, headerSort: false,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        },
        {
            title: "Associate Company", field: "associate_company.name", headerSort: false,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Location Name", field: "location.name", headerSort: false,
            formatter: reactFormatter(<CellTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Status", field: "status", headerSort: false,
            formatter: reactFormatter(<AuditStatusTmpl />),
            titleFormatter: reactFormatter(<TitleTmpl />),
            widthGrow: 1
        },
        {
            title: "Actions", hozAlign: "center", width: 100,
            headerSort: false,
            formatter: reactFormatter(<ActionColumnElements />),
            titleFormatter: reactFormatter(<TitleTmpl />)
        }
    ]

    const [tableConfig] = useState({
        paginationMode: 'remote',
        ajaxRequestFunc,
        columns,
        rowHeight: 48,
        selectable: false,
        paginate: true,
        initialSort: ['month desc'],
        rowFormatter
    });

    function rowClick(e:any, selected: any) {
      const audit = selected.getData() || {};
      if (audit.id) {
        navigate(`/location-audits/${audit.id}`, {
          state: {
            ...(previousFiltersRef.current || {})
          }
        });
      }
    }

    function rowFormatter(row: any) {
        const data = row.getData();
        const element = row.getElement();
        if (data.published) {
            // element.classList.add('activity-published');
        }
    }

    function formatApiResponse(params: any, list: any, totalRecords: number) {
        const { page, per } = params || {};
        const tdata = {
            data: list,
            total: totalRecords,
            last_page: Math.ceil(totalRecords / (per || 1)) || 1,
            page: page || 1
        };
        setData(tdata);
        return tdata;
    }

    function hasFilters(ref: any, field = 'company_id_eq') {
        const _filters: any = (ref ? ref.current : ({ ...(payloadRef.current || {}) } as any)['filters']) || {};
        const company = _filters[field];
        return company
    }

    function ajaxRequestFunc(url: any, config: any, params: any) {
        const { field, dir } = (params.sort || [])[0] || {};
        const _params = {
            per: params.size,
            page: params.page,
            include: ['company', 'location', 'associate_company', 'preliminary_report', 'final_report'],
            includes: ['company', 'location', 'associate_company', 'preliminary_report', 'final_report']
        };
        setParams(_params);
        setPayload({
            ...DEFAULT_PAYLOAD,
            ..._params,
            filters: {
                ...(lfRef.current || []),
                ...(afRef.current || []),
                ...(sfRef.current || []),
                ...AuditTypeFilter,
                status_not_eq: LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_CREATED
            }
        });
        return Promise.resolve(formatApiResponse(params, activities, total));
    }

    function handlePageNav(_pagination: any) {
        const _params = { ...params, ..._pagination };
        setParams({ ..._params });
        setPayload({ ...payload, ..._params });
    }

    useEffect(() => {
        if (locationFilters) {
            setPayload({
                ...DEFAULT_PAYLOAD,
                ...params,
                filters: {
                    ...locationFilters,
                    ...(afRef.current || {}),
                    ...(sfRef.current || {}),
                    ...AuditTypeFilter,
                    status_not_eq: LOCATION_AUDIT_STATUS.PRELIMINARY_AUDIT_CREATED
                }
            });
        }
    }, [locationFilters]);

    useEffect(() => {
        if (!isFetching && payload) {
            setData(formatApiResponse(params, activities, total));
        }
    }, [isFetching])

    return (
        <>
            <div className="d-flex flex-column">
                <div className="d-flex  p-2 align-items-center pageHeading shadow">
                    <h4 className="mb-0">Location Audits</h4>
                    <div className="d-flex align-items-end h-100">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0 d-flex justify-content-end">
                                <li className="breadcrumb-item">Home</li>
                                {
                                    fromDashboard &&
                                    <li className="breadcrumb-item fw-bold"><Link href="/dashboard">Dashboard</Link></li>
                                }
                                <li className="breadcrumb-item  fw-bold active">Audit Compliances</li>
                            </ol>
                        </nav>
                    </div>
                </div>

                <form className="p-0 mx-3 my-2">
                    <div className="card shadow border-0 p-2 mt-2 mb-3">
                        <div className="d-flex flex-row filters">
                            <Location onChange={onLocationChange} previousFilters={previousFilters} />
                        </div>
                    </div>
                </form>
                <div className="position-relative clickable">
                    <Table data={data} options={tableConfig} isLoading={isFetching} rowClick={rowClick} onPageNav={handlePageNav} />
                </div>
            </div>

            {(submitting || exporting) && <PageLoader />}
        </>
    ); 
}

export default AuditList;
